import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { createDashboardExport } from "../../../api/mutations";
import { fetchDashboardExport } from "../../../api/queries";
import { useLanguage } from "../../../contexts/LanguageContext";

const POLL_MS = 2000;
// ~3 minutes of polling, then the export is reported as failed rather than
// spinning forever on a job that died.
const MAX_POLLS = 90;

const isExportReady = (exp) => !!exp?.download_url;
const isExportFailed = (exp) => exp?.status === "failed";

// CSV export as a background job: POST starts it, then the status is polled
// until the API hands back a signed download_url (valid 24 hours).
export default function ExportButton({ storeId, type, segment, variant = "primary" }) {
  const { t } = useLanguage();
  const [exportId, setExportId] = useState(null);
  const [polls, setPolls] = useState(0);

  const start = useMutation({
    mutationFn: () => createDashboardExport({ storeId, type, segment }),
    onSuccess: (data) => {
      setPolls(0);
      setExportId(data?.id ?? null);
    },
  });

  const status = useQuery({
    queryKey: ["dashboard", "export", storeId, exportId],
    queryFn: async () => {
      setPolls((n) => n + 1);
      return fetchDashboardExport(storeId, exportId);
    },
    enabled: !!storeId && !!exportId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (isExportReady(data) || isExportFailed(data) || query.state.error) return false;
      if (query.state.dataUpdateCount >= MAX_POLLS) return false;
      return POLL_MS;
    },
  });

  const data = status.data;
  const timedOut = !!exportId && polls >= MAX_POLLS && !isExportReady(data);
  const failed = start.isError || status.isError || isExportFailed(data) || timedOut;
  const working = start.isPending || (!!exportId && !isExportReady(data) && !failed);

  const reset = () => {
    setExportId(null);
    setPolls(0);
    start.reset();
  };

  const base =
    "inline-flex min-h-11 items-center gap-2 rounded-[10px] px-4 text-sm font-semibold whitespace-nowrap";
  const look =
    variant === "primary"
      ? "bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-60"
      : "border border-gray-300 bg-white text-gray-900 hover:border-gray-400 disabled:opacity-60";

  if (isExportReady(data)) {
    return (
      <span className="inline-flex flex-wrap items-center gap-2" role="status">
        <a href={data.download_url} className={`${base} ${look}`} download>
          <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
          {t("dashboard.export.download")}
        </a>
        <button type="button" onClick={reset} className="text-[13px] font-medium text-gray-600 hover:text-gray-900">
          {t("dashboard.export.again")}
        </button>
      </span>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => {
          if (failed) reset();
          start.mutate();
        }}
        disabled={working}
        className={`${base} ${look}`}
      >
        <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
        {working ? t("dashboard.export.preparing") : t("dashboard.export.button")}
      </button>
      {failed && (
        <span role="alert" className="text-[13px] text-red-700">
          {t("dashboard.export.failed")}
        </span>
      )}
    </span>
  );
}
